import moment from 'moment';
import {DrivingEventHistory} from '../../models/eventRequest';

/**
 * Rôle d'une opération d'audit dans le planning d'un moniteur pour le créneau recherché :
 * - in   : l'heure arrive sur le créneau du moniteur (création, déplacement, réaffectation)
 * - out  : l'heure quitte le créneau du moniteur (suppression, déplacement, réaffectation)
 * - stay : l'heure reste sur le créneau du moniteur (changement d'élève, de boîte...)
 */
export type SlotRole = 'in' | 'out' | 'stay';

export interface SlotOccupant {
  eventId: number;
  studentName: string | null;
}

/** Heure de conduite actuellement active (données réelles, hors audit). */
export interface ActiveSlotEvent extends SlotOccupant {
  monitorId: number;
}

export interface MonitorTimelineEntry {
  history: DrivingEventHistory;
  role: SlotRole;
}

export interface MonitorTimeline {
  monitorId: number;
  monitorName: string;
  entries: MonitorTimelineEntry[];
  /** Nombre d'heures distinctes concernées. */
  eventCount: number;
  /** Nombre d'opérations d'audit concernées. */
  operationCount: number;
  /** Occupation reconstruite en rejouant l'audit. */
  replayedOccupants: SlotOccupant[];
  /** Occupation réelle (heures actives), null si non vérifiée. */
  actualOccupants: SlotOccupant[] | null;
  /** Faux si l'occupation réelle diffère de l'audit (ex : heure sans historique). */
  consistent: boolean;
}

/** Clé de comparaison d'un créneau : minute, en UTC (même convention que la saisie des heures). */
export function slotKey(date: string | null | undefined): string {
  return date ? moment.utc(date).format('YYYY-MM-DD HH:mm') : '';
}

/** Ordre chronologique déterministe : date d'opération puis id d'insertion. */
export function compareHistory(a: DrivingEventHistory, b: DrivingEventHistory): number {
  return moment(a.modifiedAt).valueOf() - moment(b.modifiedAt).valueOf() || a.id - b.id;
}

/** Rôle de l'opération pour le couple (créneau, moniteur), null si elle ne le concerne pas. */
export function roleFor(h: DrivingEventHistory, slot: string, monitorId: number): SlotRole | null {
  const before = slotKey(h.oldStartAt) === slot && h.oldMonitorId === monitorId;
  const after = slotKey(h.newStartAt) === slot && h.newMonitorId === monitorId;
  if (before && after) {
    return 'stay';
  }
  return after ? 'in' : (before ? 'out' : null);
}

/**
 * Construit une chronologie indépendante par moniteur pour un créneau.
 * Une même opération peut apparaître chez deux moniteurs (réaffectation) sans être dupliquée.
 */
export function buildMonitorTimelines(histories: DrivingEventHistory[], slot: string, monitorFilter: number | null,
                                      monitorNames: Map<number, string>,
                                      activeEvents: ActiveSlotEvent[] | null): MonitorTimeline[] {
  const sorted = [...histories].sort(compareHistory);

  const monitorIds = new Set<number>();
  sorted.forEach(h => {
    if (slotKey(h.oldStartAt) === slot && h.oldMonitorId != null) {
      monitorIds.add(h.oldMonitorId);
    }
    if (slotKey(h.newStartAt) === slot && h.newMonitorId != null) {
      monitorIds.add(h.newMonitorId);
    }
  });
  // Heures actives sans historique : elles doivent aussi apparaître
  (activeEvents || []).forEach(e => monitorIds.add(e.monitorId));

  return Array.from(monitorIds)
    .filter(id => monitorFilter == null || id === monitorFilter)
    .map(id => buildTimeline(id, sorted, slot, monitorNames, activeEvents))
    .sort((a, b) => a.monitorName.localeCompare(b.monitorName));
}

function buildTimeline(monitorId: number, sorted: DrivingEventHistory[], slot: string,
                       monitorNames: Map<number, string>, activeEvents: ActiveSlotEvent[] | null): MonitorTimeline {
  const entries: MonitorTimelineEntry[] = [];
  const occupants = new Map<number, string | null>();

  sorted.forEach(history => {
    const role = roleFor(history, slot, monitorId);
    if (!role) {
      return;
    }
    entries.push({history, role});
    if (role === 'out') {
      occupants.delete(history.eventId);
    } else {
      occupants.set(history.eventId, history.newStudentName || null);
    }
  });

  const replayedOccupants = toOccupants(occupants);
  const actualOccupants = activeEvents == null ? null : activeEvents
    .filter(e => e.monitorId === monitorId)
    .map(e => ({eventId: e.eventId, studentName: e.studentName}))
    .sort((a, b) => a.eventId - b.eventId);

  return {
    monitorId,
    monitorName: monitorNames.get(monitorId) || historyName(monitorId, sorted) || `Moniteur n°${monitorId}`,
    entries,
    eventCount: new Set(entries.map(e => e.history.eventId)).size,
    operationCount: entries.length,
    replayedOccupants,
    actualOccupants,
    consistent: actualOccupants == null || sameEvents(replayedOccupants, actualOccupants)
  };
}

function toOccupants(occupants: Map<number, string | null>): SlotOccupant[] {
  return Array.from(occupants.entries())
    .map(([eventId, studentName]) => ({eventId, studentName}))
    .sort((a, b) => a.eventId - b.eventId);
}

function sameEvents(a: SlotOccupant[], b: SlotOccupant[]): boolean {
  return a.length === b.length && a.every((o, i) => o.eventId === b[i].eventId);
}

/** Nom du moniteur tel qu'enregistré dans l'audit (si le moniteur n'existe plus). */
function historyName(monitorId: number, histories: DrivingEventHistory[]): string | null {
  for (let i = histories.length - 1; i >= 0; i--) {
    const h = histories[i];
    if (h.newMonitorId === monitorId && h.newMonitorName) {
      return h.newMonitorName;
    }
    if (h.oldMonitorId === monitorId && h.oldMonitorName) {
      return h.oldMonitorName;
    }
  }
  return null;
}
